export type ProductStatus = "pending" | "approved" | "rejected" | "needs_revision" | "active" | "out_of_stock";

export interface ProductImage {
  imageUrl: string;
  altText?: string;
  displayOrder: number;
  isPrimary: boolean;
}

/** Product pricing fields per business spec (MRP, DP, GST, WP, RP, CP, DC, BV) */
export interface ProductPricing {
  mrp: number;
  dp: number;
  gstPercent: number;
  wholesalePrice: number;
  retailPrice: number;
  customerPrice: number;
  deliveryCharge: number;
  businessVolume: number;
}

export interface UnifiedProduct {
  id: string;
  storeId: string;
  storeName: string;
  categoryId: string;
  categoryName: string;
  name: string;
  slug: string;
  description?: string;
  shortDescription?: string;
  mrp: number;
  sellingPrice: number;
  dp: number;
  gstPercent: number;
  wholesalePrice: number;
  retailPrice: number;
  customerPrice: number;
  deliveryCharge: number;
  businessVolume: number;
  stockQuantity: number;
  lowStockThreshold: number;
  sku?: string;
  barcode?: string;
  unit: string;
  unitValue: number;
  isFeatured: boolean;
  isVeg: boolean;
  isActive: boolean;
  tags?: string;
  brand?: string;
  weight: number;
  status: ProductStatus;
  rejectionReason?: string;
  submittedDate: string;
  images: ProductImage[];
  sold: number;
  rating: number;
  reviewCount: number;
}

export interface ProductFormData {
  name: string;
  description: string;
  categoryId: string;
  mrp: number;
  dp: number;
  gstPercent: number;
  wholesalePrice: number;
  retailPrice: number;
  customerPrice: number;
  deliveryCharge: number;
  distributorCommissionPercent: number;
  wholesaleCommissionPercent: number;
  retailCommissionPercent: number;
  businessVolume: number;
  sellingPrice: number;
  stockQuantity: number;
  unit: string;
  sku: string;
  isVeg: boolean;
  tags: string;
  imageUrl: string;
  imageUrls: string[];
}

export const DEFAULT_PRODUCT_FORM: ProductFormData = {
  name: "",
  description: "",
  categoryId: "1",
  mrp: 0,
  dp: 0,
  gstPercent: 18,
  wholesalePrice: 0,
  retailPrice: 0,
  customerPrice: 0,
  deliveryCharge: 0,
  distributorCommissionPercent: 0,
  wholesaleCommissionPercent: 0,
  retailCommissionPercent: 0,
  businessVolume: 0,
  sellingPrice: 0,
  stockQuantity: 0,
  unit: "piece",
  sku: "",
  isVeg: true,
  tags: "",
  imageUrl: "",
  imageUrls: [],
};

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function deriveStoreStatus(product: UnifiedProduct): "active" | "pending" | "rejected" | "needs_revision" | "out_of_stock" {
  if (product.status === "rejected") return "rejected";
  if (product.status === "needs_revision") return "needs_revision";
  if (product.status === "pending") return "pending";
  if (product.stockQuantity <= 0) return "out_of_stock";
  return "active";
}

export function defaultPricingFromMrp(mrp: number, customerPrice?: number): Pick<ProductFormData, "dp" | "wholesalePrice" | "retailPrice" | "customerPrice" | "deliveryCharge" | "businessVolume" | "sellingPrice"> {
  const cp = customerPrice ?? mrp;
  return {
    dp: Math.round(mrp * 0.85),
    wholesalePrice: Math.round(mrp * 0.75),
    retailPrice: Math.round(mrp * 0.9),
    customerPrice: cp,
    sellingPrice: cp,
    deliveryCharge: 20,
    businessVolume: Math.round(cp * 0.1),
  };
}
